import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import { Alert, Box, TextField, Typography } from "@mui/material";
import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ComercialDrawer from "../../../../components/general/ComercialDrawer";
import WorkingScaleSelector from "../../../../components/comercial/WorkingScaleSelector";
import { POSSIBLE_WORKING_TIMES } from "../../workingTimeConstants";
import { PrimaryButton } from "../../../../components/general/buttons/PrimaryButton";
import { IStore } from "../../../../services/api/Store/types";
import { LeftMarginBackButton } from "../../../../components/general/buttons/BackPageButton";
import { sharedStyles } from "../../../../styles/shared";
import GalleryImageList from "../../../../components/comercial/ImageGallery";
import { getLocalStorageItem } from "../../../../hooks/localStorageHooks";
import fetchCep from "../../../../hooks/useCep";
import ImageGalleryContainer from "../../../../components/comercial/ImageGallery/ImageGalleryContainer";
import Tiptap from "@/components/general/Tiptap";
import EmptyGallery from "@/components/comercial/ImageGallery/EmptyGallery";
import { handleDeleteExistingOriginalAndPreviewImageClick } from "@/utils/image/imageFunctions";
import {
  validateCnpj,
  validatePhoneNumber,
  addAndEditStoreSchema,
} from "../agencyValidationSchemas";
import AddImageInput from "@/components/comercial/AddImageInput";
import MaskedTextField from "@/components/general/MaskedTextField";
import { CEP_MASK, CNPJ_MASK, PHONE_MASK } from "@/utils/stringUtils";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import CoordinateSelector from "@/components/general/CoordinateSelector";
import { DEFAULT_COORDINATES } from "../AddAgency";
import { focusRefOnError, handleUpdateStore } from "./functions";
import DOMPurify from "dompurify";
import EditIcon from "@mui/icons-material/Edit";
import { STORE_OPERATION_STEPS } from "../AddAgency/functions";

const EditAgency = () => {
  const navigate = useNavigate();

  const selectedAgency: IStore = getLocalStorageItem("selectedStore")!;

  const cnpjErrorRef = useRef<HTMLInputElement>(null);
  const phoneErrorRef = useRef<HTMLInputElement>(null);

  const [cnpj, setCnpj] = useState<string>(selectedAgency.CNPJ);
  const [phone, setPhone] = useState<string>(selectedAgency.phone);
  const descriptionRef = useRef("");

  const [cep, setCep] = useState<string>(selectedAgency.CEP);

  const streetRef = useRef<HTMLInputElement>(null);
  const numberRef = useRef<HTMLInputElement>(null);
  const districtRef = useRef<HTMLInputElement>(null);
  const cityRef = useRef<HTMLInputElement>(null);
  const stateRef = useRef<HTMLInputElement>(null);

  const [coordinates, setCoordinates] =
    useState<[number, number]>(DEFAULT_COORDINATES);

  const foundedCepAddress = fetchCep(
    cep,
    streetRef,
    districtRef,
    cityRef,
    stateRef,
    true
  );

  const [newImages, setNewImages] = useState<HTMLCanvasElement[]>([]);

  const [originalImageUrls, setOriginalImageUrls] = useState<string[]>(
    selectedAgency.imageOriginal
  );
  const [previewImageUrls, setPreviewImageUrls] = useState<string[]>(
    selectedAgency.imagePreview
  );

  const [timeMonday, setTimeMonday] = useState<string[]>(
    selectedAgency.timeMonday
  );
  const [timeTuesday, setTimeTuesday] = useState<string[]>(
    selectedAgency.timeTuesday
  );
  const [timeWednesday, setTimeWednesday] = useState<string[]>(
    selectedAgency.timeWednesday
  );
  const [timeThursday, setTimeThursday] = useState<string[]>(
    selectedAgency.timeThursday
  );
  const [timeFriday, setTimeFriday] = useState<string[]>(
    selectedAgency.timeFriday
  );
  const [timeSaturday, setTimeSaturday] = useState<string[]>(
    selectedAgency.timeSaturday
  );
  const [timeSunday, setTimeSunday] = useState<string[]>(
    selectedAgency.timeSunday
  );

  const [submittedOnce, setSubmittedOnce] = useState<boolean>(false);

  const isCnpjValidated = useMemo(() => validateCnpj(cnpj), [cnpj]);
  const isPhoneValidated = useMemo(() => validatePhoneNumber(phone), [phone]);

  const [updateLoading, setUpdateLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit: onSubmit,
    formState: { errors },
    getValues,
  } = useForm({ resolver: yupResolver(addAndEditStoreSchema) });

  const formValues = getValues();

  const handleUpdateStoreClick = async () => {
    if (!submittedOnce) setSubmittedOnce(true);

    setUpdateLoading(true);
    setError(null);

    focusRefOnError(cnpjErrorRef, !isCnpjValidated);
    focusRefOnError(phoneErrorRef, !isPhoneValidated);

    handleUpdateStore(
      selectedAgency.id,
      {
        name: formValues.name,
        CNPJ: cnpj,
        email: formValues.email,
        phone: phone,
        description: DOMPurify.sanitize(descriptionRef.current),
        CEP: cep,
        logradouro: streetRef.current.value,
        number: numberRef.current.value,
        district: districtRef.current.value,
        city: cityRef.current.value,
        state: stateRef.current.value,
        lat: coordinates[0],
        lng: coordinates[1],
        images: newImages,
        imageOriginal: selectedAgency.imageOriginal,
        imagePreview: selectedAgency.imagePreview,
        timeMonday,
        timeTuesday,
        timeWednesday,
        timeThursday,
        timeFriday,
        timeSaturday,
        timeSunday,
      },
      originalImageUrls,
      previewImageUrls
    )
      .then((response) => {
        alert(`Agência ${response.name} editada com sucesso!`);
        navigate("/agencies");
      })
      .catch((error) => setError(error.response.data.message))
      .finally(() => setUpdateLoading(false));
  };

  const renderStoreDetails = (
    <Box sx={sharedStyles.verticalContent}>
      <Box sx={sharedStyles.dividedFormContainer}>
        <TextField
          size="small"
          label="Nome da agência *"
          defaultValue={selectedAgency.name}
          {...register("name")}
          error={!!errors.name}
          helperText={errors.name?.message}
          fullWidth
        />

        <MaskedTextField
          label="CNPJ *"
          mask={CNPJ_MASK}
          value={cnpj}
          setValue={setCnpj}
          maskChar={"_"}
          error={!isCnpjValidated && submittedOnce}
          helperText={!isCnpjValidated && submittedOnce && "CNPJ inválido."}
          inputRef={cnpjErrorRef}
        />
      </Box>

      <Box sx={sharedStyles.dividedFormContainer}>
        <TextField
          size="small"
          label="Email para contato *"
          placeholder="Ex.: contato@email.com"
          fullWidth
          defaultValue={selectedAgency.email}
          {...register("email")}
          error={!!errors.email}
          helperText={errors.email?.message}
        />

        <MaskedTextField
          label="Telefone para contato *"
          mask={PHONE_MASK}
          maskChar={"_"}
          value={phone}
          setValue={setPhone}
          error={!isPhoneValidated && submittedOnce}
          helperText={
            !isPhoneValidated && submittedOnce && "Telefone inválido."
          }
          inputRef={phoneErrorRef}
        />
      </Box>

      <Tiptap
        defaultValue={selectedAgency.description}
        onChange={(e) => (descriptionRef.current = e)}
        placeholder="Descreva brevemente sua agência..."
      />
    </Box>
  );

  const renderGeographicData = (
    <Box sx={sharedStyles.verticalContent}>
      <MaskedTextField
        label="CEP"
        mask={CEP_MASK}
        value={cep}
        setValue={setCep}
        maskChar={"_"}
      />

      <Box sx={sharedStyles.horizontalContent}>
        <TextField
          size="small"
          label="Logradouro"
          defaultValue={selectedAgency?.logradouro}
          inputRef={streetRef}
          InputLabelProps={{ shrink: true }}
          disabled={
            foundedCepAddress?.street !== null &&
            streetRef?.current?.value !== ""
          }
          fullWidth
        />
        <TextField
          size="small"
          label="N°"
          defaultValue={selectedAgency?.number}
          inputRef={numberRef}
          sx={{ width: "35%" }}
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      <TextField
        size="small"
        label="Bairro"
        defaultValue={selectedAgency?.district}
        fullWidth
        inputRef={districtRef}
        InputLabelProps={{ shrink: true }}
        disabled={
          foundedCepAddress?.neighborhood !== null &&
          districtRef?.current?.value !== ""
        }
      />

      <Box sx={sharedStyles.dividedFormContainer}>
        <TextField
          label="Cidade"
          inputRef={cityRef}
          InputLabelProps={{ shrink: true }}
          fullWidth
          disabled={
            foundedCepAddress?.city !== null && cityRef?.current?.value !== ""
          }
          defaultValue={selectedAgency?.city}
          size="small"
        />

        <TextField
          label="Estado"
          inputRef={stateRef}
          InputLabelProps={{ shrink: true }}
          fullWidth
          disabled={
            foundedCepAddress?.state !== null && stateRef?.current?.value !== ""
          }
          defaultValue={selectedAgency?.state}
          size="small"
          sx={{ width: { xs: "100%", md: "35%", lg: "35%" } }}
        />
      </Box>

      {foundedCepAddress && (
        <Box sx={{ alignSelf: "flex-end", marginTop: 3 }}>
          <CoordinateSelector
            coordinates={coordinates}
            setCoordinates={setCoordinates}
            brazilianAddress={foundedCepAddress}
          />
        </Box>
      )}
    </Box>
  );

  const renderImageGalery = (
    <>
      <Box
        sx={{
          ...sharedStyles.horizontalContent,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          {STORE_OPERATION_STEPS[2]}
        </Typography>
        <AddImageInput
          setImages={setNewImages}
          icon={<AddPhotoAlternateIcon color="secondary" />}
        />
      </Box>

      {originalImageUrls.length === 0 && newImages.length === 0 ? (
        <EmptyGallery />
      ) : (
        <ImageGalleryContainer>
          <GalleryImageList
            images={originalImageUrls}
            setImages={setOriginalImageUrls}
            removeImageFunction={(index) =>
              handleDeleteExistingOriginalAndPreviewImageClick(
                index,
                setOriginalImageUrls,
                setPreviewImageUrls
              )
            }
            interable
          />
          <GalleryImageList
            images={newImages}
            setImages={setNewImages}
            interable
          />
        </ImageGalleryContainer>
      )}
    </>
  );

  const renderWorkScaleSelector = (
    <>
      <WorkingScaleSelector
        timeMonday={timeMonday}
        setTimeMonday={setTimeMonday}
        timeTuesday={timeTuesday}
        setTimeTuesday={setTimeTuesday}
        timeWednesday={timeWednesday}
        setTimeWednesday={setTimeWednesday}
        timeThursday={timeThursday}
        setTimeThursday={setTimeThursday}
        timeFriday={timeFriday}
        setTimeFriday={setTimeFriday}
        timeSaturday={timeSaturday}
        setTimeSaturday={setTimeSaturday}
        timeSunday={timeSunday}
        setTimeSunday={setTimeSunday}
        possibleMondayTimes={POSSIBLE_WORKING_TIMES}
        possibleTuesdayTimes={POSSIBLE_WORKING_TIMES}
        possibleWednesdayTimes={POSSIBLE_WORKING_TIMES}
        possibleThursdayTimes={POSSIBLE_WORKING_TIMES}
        possibleFridayTimes={POSSIBLE_WORKING_TIMES}
        possibleSaturdayTimes={POSSIBLE_WORKING_TIMES}
        possibleSundayTimes={POSSIBLE_WORKING_TIMES}
      />
    </>
  );

  const renderEditStorePageContent = (
    <>
      <LeftMarginBackButton />

      <form onSubmit={onSubmit(handleUpdateStoreClick)}>
        <Box sx={{ ...sharedStyles.verticalContent, gap: 4 }}>
          <Typography variant="h6" fontWeight="bold">
            {STORE_OPERATION_STEPS[0]}
          </Typography>

          {renderStoreDetails}

          <Typography variant="h6" fontWeight="bold">
            {STORE_OPERATION_STEPS[1]}
          </Typography>

          {renderGeographicData}

          {renderImageGalery}

          <Typography variant="h6" fontWeight="bold">
            {STORE_OPERATION_STEPS[3]}
          </Typography>

          {renderWorkScaleSelector}
        </Box>

        <Box sx={{ ...sharedStyles.verticalContent, marginTop: 6 }}>
          {error && (
            <Alert severity="error" sx={{ marginY: 2 }}>
              {error}
            </Alert>
          )}

          <PrimaryButton
            label="Confirmar edição"
            styles={sharedStyles.secondaryColor}
            icon={<EditIcon />}
            isLoading={updateLoading}
          />
        </Box>
      </form>
    </>
  );

  return <ComercialDrawer content={renderEditStorePageContent} />;
};

export default EditAgency;
